import type { FeatureCollection, GeoFeature } from "./types";

type Point = [number, number];

const allPoints = (feature: GeoFeature): Point[] => {
  if (feature.geometry.type === "Polygon") {
    return (feature.geometry.coordinates as number[][][]).flat() as Point[];
  }
  return (feature.geometry.coordinates as number[][][][]).flat(2) as Point[];
};

export function makeProjector(collection: FeatureCollection, width = 900, height = 420) {
  const points = collection.features.flatMap(allPoints);
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const padding = 26;
  const scale = Math.min((width - padding * 2) / (maxX - minX), (height - padding * 2) / (maxY - minY));
  const offsetX = (width - (maxX - minX) * scale) / 2;
  const offsetY = (height - (maxY - minY) * scale) / 2;

  return ([x, y]: Point): Point => [
    offsetX + (x - minX) * scale,
    height - (offsetY + (y - minY) * scale),
  ];
}

const ringPath = (ring: number[][], project: (point: Point) => Point) =>
  `${ring
    .map((point, index) => {
      const [x, y] = project(point as Point);
      return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ")} Z`;

export function featurePath(feature: GeoFeature, project: (point: Point) => Point) {
  if (feature.geometry.type === "Polygon") {
    return (feature.geometry.coordinates as number[][][]).map((ring) => ringPath(ring, project)).join(" ");
  }
  return (feature.geometry.coordinates as number[][][][])
    .flatMap((polygon) => polygon.map((ring) => ringPath(ring, project)))
    .join(" ");
}
